import { appleTrackIdFrom, spotifyTrackIdFrom, tidalTrackIdFrom } from './songIdentity';

export type LinkProvider = 'spotify' | 'tidal' | 'apple';
export type NotTrackKind = 'album' | 'playlist' | 'artist' | 'episode' | 'show' | 'other';

export type ParsedLink =
  | { kind: 'track'; provider: LinkProvider; trackId: string }
  | { kind: 'short'; provider: 'spotify'; url: string }
  | { kind: 'not_track'; provider: LinkProvider; what: NotTrackKind }
  | { kind: 'unsupported' };

export type CleanedLink =
  | { ok: true; provider: LinkProvider; trackId: string; url: string; platformName: string }
  | { ok: false; message: string };

const SCHEMELESS_MUSIC_HOST = /^(?:open\.spotify\.com|spotify\.link|spotify\.app\.link|(?:listen\.)?tidal\.com|music\.apple\.com)\//i;
const SPOTIFY_SHORT = /^https?:\/\/(spotify\.link|spotify\.app\.link)\/([A-Za-z0-9_-]+)/i;
const SPOTIFY_WEB_NON_TRACK = /open\.spotify\.com\/(?:intl-[a-z]{2}(?:-[a-z]{2})?\/)?(album|playlist|artist|episode|show)\//i;
const SPOTIFY_URI_NON_TRACK = /^spotify:(album|playlist|artist|episode|show):/i;
const SPOTIFY_WEB_HOST = /^https?:\/\/open\.spotify\.com\//i;
const TIDAL_NON_TRACK = /tidal\.com\/(?:browse\/)?(album|playlist|artist|mix|video)\//i;
const TIDAL_HOST = /^https?:\/\/(?:listen\.|www\.)?tidal\.com\//i;
const APPLE_NON_TRACK = /music\.apple\.com\/(?:[a-z]{2}\/)?(album|playlist|artist)\//i;
const APPLE_HOST = /^https?:\/\/music\.apple\.com\//i;
const APPLE_ID = /^\d+$/;

export const UNSUPPORTED_LINK_MESSAGE = 'Paste a Spotify, TIDAL or Apple Music track link.';
export const SHORT_LINK_FAILED_MESSAGE = "Couldn't open that Spotify short link — paste the full track link";

function notTrackKind(value: string | undefined): NotTrackKind {
  const lower = (value ?? '').toLowerCase();
  if (lower === 'album' || lower === 'playlist' || lower === 'artist' || lower === 'episode' || lower === 'show') {
    return lower;
  }
  return 'other';
}

export function parseMusicLink(raw: string): ParsedLink {
  const trimmed = typeof raw === 'string' ? raw.trim() : '';
  if (!trimmed) return { kind: 'unsupported' };
  const link = SCHEMELESS_MUSIC_HOST.test(trimmed) ? `https://${trimmed}` : trimmed;

  const shortMatch = link.match(SPOTIFY_SHORT);
  if (shortMatch) {
    return { kind: 'short', provider: 'spotify', url: `https://${shortMatch[1].toLowerCase()}/${shortMatch[2]}` };
  }

  const spotifyId = spotifyTrackIdFrom(link);
  if (spotifyId) return { kind: 'track', provider: 'spotify', trackId: spotifyId };
  const spotifyNonTrack = link.match(SPOTIFY_URI_NON_TRACK) ?? link.match(SPOTIFY_WEB_NON_TRACK);
  if (spotifyNonTrack) return { kind: 'not_track', provider: 'spotify', what: notTrackKind(spotifyNonTrack[1]) };
  if (SPOTIFY_WEB_HOST.test(link)) return { kind: 'not_track', provider: 'spotify', what: 'other' };

  const tidalId = tidalTrackIdFrom(link);
  if (tidalId) return { kind: 'track', provider: 'tidal', trackId: tidalId };
  const tidalNonTrack = link.match(TIDAL_NON_TRACK);
  if (tidalNonTrack) return { kind: 'not_track', provider: 'tidal', what: notTrackKind(tidalNonTrack[1]) };
  if (TIDAL_HOST.test(link)) return { kind: 'not_track', provider: 'tidal', what: 'other' };

  const appleId = appleTrackIdFrom(link);
  if (appleId) return { kind: 'track', provider: 'apple', trackId: appleId };
  const appleNonTrack = link.match(APPLE_NON_TRACK);
  if (appleNonTrack) return { kind: 'not_track', provider: 'apple', what: notTrackKind(appleNonTrack[1]) };
  if (APPLE_HOST.test(link)) return { kind: 'not_track', provider: 'apple', what: 'other' };

  return { kind: 'unsupported' };
}

export function canonicalTrackUrl(provider: LinkProvider, trackId: string): string {
  if (provider === 'spotify') return `https://open.spotify.com/track/${trackId}`;
  if (provider === 'tidal') return `https://tidal.com/browse/track/${trackId}`;
  return `https://music.apple.com/us/song/${trackId}`;
}

export function platformNameOf(provider: LinkProvider): string {
  if (provider === 'spotify') return 'Spotify';
  if (provider === 'tidal') return 'TIDAL';
  return 'Apple Music';
}

export function notTrackMessage(provider: LinkProvider, what: NotTrackKind): string {
  const name = platformNameOf(provider);
  if (what === 'other') return `That's not a ${name} track link. Paste a link to a single track.`;
  return `That's a ${name} ${what} link. Paste a link to a single track.`;
}

export function isValidAppleId(value: unknown): boolean {
  if (typeof value !== 'string' && typeof value !== 'number') return false;
  const text = String(value);
  return APPLE_ID.test(text) && text !== '0';
}

export async function getSongMatchHash(title: string, artist: string): Promise<string> {
  const normalized = `${title.toLowerCase().trim()}|${artist.toLowerCase().trim()}`;
  const encoded = new TextEncoder().encode(normalized);
  const hashBuffer = await crypto.subtle.digest('SHA-256', encoded);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  return hashHex.substring(0, 40);
}

export async function songLinkKey(song: {
  title: string;
  artist: string;
  appleId?: string | number | null;
}): Promise<string> {
  if (isValidAppleId(song.appleId)) return `apple_${String(song.appleId)}`;
  return `title_${await getSongMatchHash(song.title, song.artist)}`;
}

export function isValidSongLinkKey(key: unknown): key is string {
  return typeof key === 'string' && /^(?:apple_\d+|title_[0-9a-f]{40})$/.test(key);
}

export function canonicalLinkOrSame(link: string): string {
  const parsed = parseMusicLink(link);
  if (parsed.kind === 'track' && parsed.provider !== 'apple') return canonicalTrackUrl(parsed.provider, parsed.trackId);
  return link;
}

function success(provider: LinkProvider, trackId: string): CleanedLink {
  return { ok: true, provider, trackId, url: canonicalTrackUrl(provider, trackId), platformName: platformNameOf(provider) };
}

export async function cleanPastedLink(
  raw: string,
  resolveShort: (url: string) => Promise<{ trackId: string; url: string }>,
): Promise<CleanedLink> {
  const parsed = parseMusicLink(raw);
  if (parsed.kind === 'track') return success(parsed.provider, parsed.trackId);
  if (parsed.kind === 'not_track') return { ok: false, message: notTrackMessage(parsed.provider, parsed.what) };
  if (parsed.kind === 'unsupported') return { ok: false, message: UNSUPPORTED_LINK_MESSAGE };

  try {
    const resolved = await resolveShort(parsed.url);
    return success('spotify', resolved.trackId);
  } catch (err) {
    const message = err instanceof Error && err.message ? err.message : SHORT_LINK_FAILED_MESSAGE;
    return { ok: false, message };
  }
}
